import { CatSagOptimizer, type CatSagResult, IkeAngle, IkeUnit } from '@ikegps/core-measurement';
import type { CaptureFileJSONObject } from '@ikegps/core-api';
import { IkeFieldType } from '@ikegps/core-api';
import type { FormModelField } from '../../form/form.field';
import { CollectionModelField } from '../collection.field';
import { registerFieldType } from './field.registry';
import { ModelMutatorNumber, NumberFieldProvider } from './number.field.mutator';
import type { CaptureProvider } from './provider/capture.provider';

export type ModelFieldStateNumber =
	| ModelFieldStateNumberManual
	| ModelFieldStateNumberBearing
	| ModelFieldStateNumberSag;

export interface ModelFieldStateNumberManual {
	provider: 'manual';
	value: number;
}

export interface ModelFieldStateNumberBearing {
	provider: 'bearing' | 'backbearing';
	// This could be either the captureId or captureUuid
	captureId: string; // CaptureId
}

export interface ModelFieldStateNumberSag {
	provider: 'sag';
	start: string; // CaptureId
	end: string; // CaptureId
	points: string[]; // CaptureId
}

export class CollectionModelFieldNumber
	extends CollectionModelField<FormModelField, ModelFieldStateNumber>
	implements CaptureProvider
{
	static readonly PROVIDER_MANUAL = NumberFieldProvider.MANUAL;
	static readonly PROVIDER_BEARING = NumberFieldProvider.BEARING;
	static readonly PROVIDER_BACK_BEARING = NumberFieldProvider.BACK_BEARING;
	static readonly PROVIDER_SAG = NumberFieldProvider.SAG;

	static TYPE: IkeFieldType = IkeFieldType.NUMBER;

	/** Type guard verify a field is of type number */
	static isNumberField(f: CollectionModelField): f is CollectionModelFieldNumber {
		return f.schema.type === CollectionModelFieldNumber.TYPE;
	}

	private _Mutator: ModelMutatorNumber = new ModelMutatorNumber(this);
	get Mutator() {
		if (this._Mutator == null) {
			this._Mutator = new ModelMutatorNumber(this);
		}
		return this._Mutator;
	}

	/**
	 * Calculate the value of the number field based off the provider type
	 * @returns Raw number value.
	 */
	override get value(): number | null {
		if (this.stateValue == null) {
			return null;
		}

		if (this.isManual()) {
			return this.stateValue.value;
		}

		if (this.isBackBearing()) {
			const capture = this.collection.state.getCapture(this.stateValue.captureId);
			return IkeAngle.normalize(capture.orientation.bearing + Math.PI);
		}

		if (this.isBearing()) {
			const capture = this.collection.state.getCapture(this.stateValue.captureId);
			return IkeAngle.normalize(capture.orientation.bearing);
		}

		if (this.isSag()) {
			const sagResult = CollectionModelFieldNumberSagUtil.getSag(this);
			if (sagResult == null || isNaN(sagResult.maxSag)) {
				return 0;
			}

			return sagResult.maxSag;
		}

		return null;
	}

	/** UI + importable Excel: bearing angles use 3dp per UFO-121 */
	override get displayValue(): string {
		return this.formatValue(IkeUnit.ANGLE_DECIMALS);
	}

	/** Other exports (KML, standard Excel, PDF) show bearing angles with 2dp per UFO-649 */
	override get exportDisplayValue(): string {
		return this.formatValue(IkeUnit.DECIMALS);
	}

	private formatValue(angleDecimals: number): string {
		if (this.isManual()) {
			if (isNaN(this.value) || typeof this.value !== 'number') {
				return '';
			}
			return String(IkeUnit.round(this.value, IkeUnit.DECIMALS));
		}

		if (this.isBackBearing() || this.isBearing()) {
			const degrees = IkeUnit.convertAngle(this.value, IkeUnit.DEGREE, IkeUnit.RADIAN);
			return String(IkeUnit.round(degrees, angleDecimals));
		}

		if (this.isSag()) {
			const sagResult = CollectionModelFieldNumberSagUtil.getSag(this);
			if (sagResult == null) {
				return '0';
			}
			return String(IkeUnit.round(sagResult.maxSag, IkeUnit.DECIMALS));
		}

		return '';
	}

	/**
	 * Get the provider type of the field.
	 * Defaults to 'manual' if no provider is defined.
	 *
	 * @returns Field provider type.
	 */
	get provider(): 'manual' | 'bearing' | 'backbearing' | 'sag' {
		const stateValue = this.stateValue;
		if (stateValue == null || stateValue.provider == null) {
			return CollectionModelFieldNumber.PROVIDER_MANUAL;
		}
		return stateValue.provider;
	}

	isManual(): this is CollectionModelField<FormModelField, ModelFieldStateNumberManual> {
		return this.provider === CollectionModelFieldNumber.PROVIDER_MANUAL;
	}

	isBearing(): this is CollectionModelField<FormModelField, ModelFieldStateNumberBearing> {
		return this.provider === CollectionModelFieldNumber.PROVIDER_BEARING;
	}

	isBackBearing(): this is CollectionModelField<FormModelField, ModelFieldStateNumberBearing> {
		return this.provider === CollectionModelFieldNumber.PROVIDER_BACK_BEARING;
	}

	isSag(): this is CollectionModelField<FormModelField, ModelFieldStateNumberSag> {
		return this.provider === CollectionModelFieldNumber.PROVIDER_SAG;
	}

	getOwnedCaptures(): CaptureFileJSONObject[] {
		return this.getCaptures();
	}

	/**
	 * All captures used by this field.
	 * @returns List of captures
	 */
	getCaptures(): CaptureFileJSONObject[] {
		return this.getCaptureIds()
			.map((c) => this.collection.state.getCapture(c))
			.filter((c) => !!c);
	}

	getOwnedCaptureIds(): string[] {
		return this.getCaptureIds();
	}

	getCaptureIds(): string[] {
		if (this.isManual()) {
			return [];
		}

		if (this.stateValue == null) {
			return [];
		}

		if (this.isSag()) {
			return [this.stateValue.start, ...this.stateValue.points, this.stateValue.end];
		}

		if (this.isBackBearing() || this.isBearing()) {
			return [this.stateValue.captureId];
		}
		return [];
	}

	/**
	 * Whether this field's value is valid
	 * @returns Whether the value is valid
	 */
	hasValue(): boolean {
		const value = this.value;
		if (value == null || isNaN(value)) {
			return false;
		}
		return true;
	}
}

export class CollectionModelFieldNumberSagUtil {
	static getSag(field: CollectionModelFieldNumber): CatSagResult | null {
		const captures = CollectionModelFieldNumberSagUtil.getSagCaptures(field);
		if (captures == null) {
			return null;
		}

		if (!captures.points[0]) {
			return null;
		}

		const sagOptimizer = CatSagOptimizer.fromCaptures(captures.start, captures.end, captures.points[0]);
		return sagOptimizer.getSag();
	}

	static getSagCaptures(
		field: CollectionModelFieldNumber
	): { start: CaptureFileJSONObject; end: CaptureFileJSONObject; points: CaptureFileJSONObject[] } | null {
		if (!field.isSag()) {
			return null;
		}
		const stateValue = field.stateValue;

		const start = field.collection.state.getCapture(stateValue.start);
		const end = field.collection.state.getCapture(stateValue.end);
		const points = stateValue.points
			.map((captureId) => field.collection.state.getCapture(captureId))
			.filter((c) => !!c);

		return { start, end, points };
	}
}

registerFieldType(IkeFieldType.NUMBER, (parent, schema) => new CollectionModelFieldNumber(parent, schema));
